import { useState, useCallback, useEffect } from 'react';
import { useDebounce } from './useDebounce';
import { findPathsToMatchingValues } from '../utils/searchUtils';
import { generateExpansionsFromPaths } from '../utils/expandPathUtils';

interface SearchResult {
    searchTerm: string;
    inputValue: string;
    debouncedSearchTerm: string;
    searchResults: Record<string, boolean>;
    matchingPaths: string[];
    singleMatchPath: string;
    handleSearchInputChange: (
        input: React.ChangeEvent<HTMLInputElement> | string,
    ) => void;
    clearSearch: () => void;
    parseSearchPath: (search: string) => string;
}

/**
 * Custom hook to handle state searching functionality
 * @param sortedState - Sorted state object to search through
 * @param setExpandedSections - Function to update expanded sections
 * @returns - Search handlers and state
 */
export const useStateSearch = (
    sortedState: Record<string, any>,
    setExpandedSections: React.Dispatch<
        React.SetStateAction<Record<string, boolean>>
    >,
): SearchResult => {
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [inputValue, setInputValue] = useState<string>('');
    const debouncedSearchTerm = useDebounce<string>(searchTerm, 500);
    const [searchResults, setSearchResults] = useState<Record<string, boolean>>(
        {},
    );
    const [matchingPaths, setMatchingPaths] = useState<string[]>([]);
    const [singleMatchPath, setSingleMatchPath] = useState<string>('');

    // Handle search input change immediately
    const handleSearchInputChange = useCallback(
        (input: React.ChangeEvent<HTMLInputElement> | string) => {
            // Handle both string input and change events
            let value = '';

            if (typeof input === 'string') {
                value = input;
            } else if (input?.target?.value !== undefined) {
                value = input.target.value;
            }

            setInputValue(value);
            // Debounce the actual search processing
            setTimeout(() => {
                setSearchTerm(value);
            }, 0);
        },
        [],
    );

    // Clear search
    const clearSearch = useCallback(() => {
        setSearchTerm('');
        setInputValue('');
        setSearchResults({});
        setMatchingPaths([]);
        setSingleMatchPath('');
    }, []);

    // Parse search term for path navigation
    const parseSearchPath = useCallback((search: string): string => {
        return search?.trim().toLowerCase() || '';
    }, []);

    // Process search separately from input handling
    useEffect(() => {
        if (!debouncedSearchTerm) {
            setSearchResults({});
            setMatchingPaths([]);
            setSingleMatchPath('');
            return;
        }

        // Parse the search path
        const searchPath = parseSearchPath(debouncedSearchTerm);

        // Wrap in try/catch to prevent uncaught errors
        try {
            // Use requestAnimationFrame to process expansion during idle time
            requestAnimationFrame(() => {
                // If it's a path search (e.g. "app.extension")
                if (searchPath.includes('.')) {
                    const pathSegments = searchPath.split('.');

                    // Auto-expand the top-level key
                    if (pathSegments.length > 0) {
                        setExpandedSections((prev) => ({
                            ...prev,
                            [pathSegments[0]]: true,
                        }));

                        // Set expanded state for nested segments - only exactly what was searched
                        // For example, if search is "app.extension", expand only "extension" under "app"
                        if (pathSegments.length > 1) {
                            const expandedPaths: Record<string, boolean> = {};
                            for (let i = 1; i < pathSegments.length; i++) {
                                expandedPaths[pathSegments[i]] = true;
                            }
                            setSearchResults(expandedPaths);
                        }
                    }
                    setSingleMatchPath('');
                } else {
                    // For value search, safely find paths to matching values
                    const paths: string[] = [];

                    // Limit the number of sections processed for better performance
                    const sectionEntries = Object.entries(sortedState).slice(
                        0,
                        20,
                    );

                    // Process each section of state to find paths to matching values
                    sectionEntries.forEach(([key, value]) => {
                        try {
                            // Use the optimized path finding function
                            const sectionPaths = findPathsToMatchingValues(
                                value,
                                searchPath,
                                key,
                            );

                            if (sectionPaths && sectionPaths.length > 0) {
                                // Add paths to the collection
                                paths.push(...sectionPaths);

                                // Auto-expand the top-level section containing matches
                                setExpandedSections((prev) => ({
                                    ...prev,
                                    [key]: true,
                                }));
                            }
                        } catch (err) {
                            console.error(
                                `Error processing section ${key}:`,
                                err,
                            );
                        }
                    });

                    // Store the matching paths (safely limit their number)
                    const limitedPaths = paths.slice(0, 100);
                    setMatchingPaths(limitedPaths);

                    // Set the single match path if there's exactly one match
                    if (paths.length === 1) {
                        // Format the path to be more readable
                        const formattedPath = paths[0].replace(
                            /\.(\d+)\./g,
                            '[$1].',
                        );
                        setSingleMatchPath(formattedPath);
                    } else {
                        setSingleMatchPath('');
                    }

                    // Generate expanded paths from full paths to matches
                    const newExpandedPaths =
                        generateExpansionsFromPaths(limitedPaths);
                    setSearchResults(newExpandedPaths);
                }
            });
        } catch (err) {
            console.error('Error in search processing:', err);
        }
    }, [
        debouncedSearchTerm,
        parseSearchPath,
        sortedState,
        setExpandedSections,
    ]);

    return {
        searchTerm,
        inputValue,
        debouncedSearchTerm,
        searchResults,
        matchingPaths,
        singleMatchPath,
        handleSearchInputChange,
        clearSearch,
        parseSearchPath,
    };
};
